/**
 * PostgREST typed accessors for products / inventory_items / stock_movements
 * and the v_low_stock view (Залиха stock management UI). RLS scopes rows to
 * the caller's tenant and role: owner/manager for products; owner/manager/
 * front_desk for inventory_items and stock_movements. No Edge Function needed
 * for these reads/writes — PostgREST handles them directly.
 */
import type { Database } from '@gym/db-types';
import { ApiError } from '@gym/shared';
import type { StockMovementType, TaxRateCode } from '@gym/shared';

import type { GymSupabaseClient } from '../supabase.js';

type ProductRow = Database['public']['Tables']['products']['Row'];
type ProductInsert = Database['public']['Tables']['products']['Insert'];
type InventoryItemRow = Database['public']['Tables']['inventory_items']['Row'];
type InventoryItemInsert = Database['public']['Tables']['inventory_items']['Insert'];

// ── Products ─────────────────────────────────────────────────────────────────

export interface ProductInput {
  name: string;
  price: number;
  cost?: number | null;
  category?: string | null;
  sku?: string | null;
  barcode?: string | null;
  tax_rate_code?: TaxRateCode;
  is_active?: boolean;
  track_inventory?: boolean;
}

/** List all products for the tenant (RLS-scoped). Optionally filter to active only. */
export async function listProducts(
  client: GymSupabaseClient,
  opts: { activeOnly?: boolean } = {},
): Promise<ProductRow[]> {
  let q = client.from('products').select('*');
  if (opts.activeOnly) q = q.eq('is_active', true);
  const { data, error } = await q.order('name');
  if (error) throw new ApiError('internal_error', { message: error.message, cause: error });
  return data ?? [];
}

/**
 * Create a product (owner/manager via RLS) and a baseline inventory row so the
 * product starts at a concrete stock of 0 (no null "—" in the Залиха table).
 * The inventory row is scoped to `locationId` (or tenant-wide when null).
 */
export async function createProduct(
  client: GymSupabaseClient,
  tenantId: string,
  input: ProductInput,
  locationId?: string | null,
): Promise<ProductRow> {
  const row: ProductInsert = { ...input, tenant_id: tenantId };
  const { data, error } = await client.from('products').insert(row).select('*').single();
  if (error) throw new ApiError('internal_error', { message: error.message, cause: error });

  const invRow: InventoryItemInsert = {
    tenant_id: tenantId,
    product_id: data.id,
    location_id: locationId ?? null,
    quantity_on_hand: 0,
    reorder_level: 0,
  };
  const { error: invError } = await client.from('inventory_items').insert(invRow);
  if (invError)
    throw new ApiError('internal_error', { message: invError.message, cause: invError });

  return data;
}

/** Update a product (owner/manager via RLS). */
export async function updateProduct(
  client: GymSupabaseClient,
  id: string,
  input: Partial<ProductInput>,
): Promise<ProductRow> {
  const { data, error } = await client
    .from('products')
    .update({ ...input, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select('*')
    .single();
  if (error) throw new ApiError('internal_error', { message: error.message, cause: error });
  return data;
}

/**
 * Delete a product from everywhere (owner/manager via RLS). Removes dependent
 * rows in FK-safe order — `stock_movements` then `inventory_items` (both
 * `product_id` = restrict) — before deleting the product itself. `sale_items`
 * is left untouched: its `product_id` FK is `SET NULL`, so sales history is
 * preserved. If the final product delete is blocked (e.g. a
 * `purchase_order_items` restrict reference), throws a `conflict`
 * ('product_has_references'); any other DB failure throws `internal_error`.
 */
export async function deleteProduct(client: GymSupabaseClient, id: string): Promise<void> {
  const movements = await client.from('stock_movements').delete().eq('product_id', id);
  if (movements.error)
    throw new ApiError('internal_error', {
      message: movements.error.message,
      cause: movements.error,
    });

  const inventory = await client.from('inventory_items').delete().eq('product_id', id);
  if (inventory.error)
    throw new ApiError('internal_error', {
      message: inventory.error.message,
      cause: inventory.error,
    });

  const product = await client.from('products').delete().eq('id', id);
  if (product.error)
    throw new ApiError('conflict', {
      message: 'product_has_references',
      cause: product.error,
    });
}

// ── Inventory items ───────────────────────────────────────────────────────────

/** All inventory items for a product (across locations). */
export async function getProductInventory(
  client: GymSupabaseClient,
  productId: string,
): Promise<InventoryItemRow[]> {
  const { data, error } = await client
    .from('inventory_items')
    .select('*')
    .eq('product_id', productId);
  if (error) throw new ApiError('internal_error', { message: error.message, cause: error });
  return data ?? [];
}

// ── Stock movements / adjustments ─────────────────────────────────────────────

export interface AdjustStockInput {
  inventoryItemId: string;
  /** Signed integer — positive for in-flow, negative for out-flow. */
  delta: number;
  movementType: StockMovementType;
  reason?: string;
  staffId: string;
}

/**
 * Record a manual stock adjustment over PostgREST (owner/manager/front_desk via
 * RLS) — works without any server-side RPC. Re-reads the live inventory row
 * (to minimise the read-modify-write window and to source the fields the
 * movement needs), guards against a negative result, inserts a signed
 * `stock_movement`, then updates `quantity_on_hand`.
 *
 * Throws `validation_failed` ('insufficient_stock') if the result would go
 * negative; `internal_error` on any other DB failure.
 */
export async function adjustStock(
  client: GymSupabaseClient,
  i: AdjustStockInput,
): Promise<{ quantity_on_hand: number }> {
  const { data: item, error: readErr } = await client
    .from('inventory_items')
    .select('id, tenant_id, product_id, location_id, quantity_on_hand')
    .eq('id', i.inventoryItemId)
    .single();
  if (readErr || !item) {
    throw new ApiError('internal_error', {
      message: readErr?.message ?? 'inventory_item_not_found',
      ...(readErr ? { cause: readErr } : {}),
    });
  }

  const next = Number(item.quantity_on_hand) + i.delta;
  if (next < 0) throw new ApiError('validation_failed', { message: 'insufficient_stock' });

  const mv = await client.from('stock_movements').insert({
    tenant_id: item.tenant_id,
    product_id: item.product_id,
    inventory_item_id: item.id,
    location_id: item.location_id,
    movement_type: i.movementType,
    quantity: i.delta,
    reason: i.reason ?? null,
    moved_by_staff_id: i.staffId,
  });
  if (mv.error)
    throw new ApiError('internal_error', { message: mv.error.message, cause: mv.error });

  const upd = await client
    .from('inventory_items')
    .update({ quantity_on_hand: next })
    .eq('id', item.id);
  if (upd.error)
    throw new ApiError('internal_error', { message: upd.error.message, cause: upd.error });

  return { quantity_on_hand: next };
}

// ── Low stock view ────────────────────────────────────────────────────────────

export interface LowStockRow {
  inventory_item_id: string;
  product_id: string;
  product_name: string;
  quantity_on_hand: number;
  threshold: number;
}

/** Items currently at or below their reorder threshold (reads v_low_stock). */
export async function getLowStock(client: GymSupabaseClient): Promise<LowStockRow[]> {
  const { data, error } = await client
    .from('v_low_stock')
    .select('inventory_item_id, product_id, product_name, quantity_on_hand, threshold')
    .order('quantity_on_hand', { ascending: true });
  if (error) throw new ApiError('internal_error', { message: error.message, cause: error });
  return (data ?? []) as unknown as LowStockRow[];
}
